import api, { UPLOAD_TIMEOUT } from './api'
import type { ApiResponse, User, PaginatedResponse, Post } from '../types'

interface UserStats {
  postsCount: number
  followersCount: number
  followingCount: number
}

interface UserProfile extends User {
  posts?: Post[]
  stats: UserStats
  isFollowing?: boolean
  isOwnProfile?: boolean
}

// Helper to transform API response to UserProfile
const transformToUserProfile = (data: any): UserProfile => ({
  ...data,
  id: data.id,
  fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
  stats: {
    postsCount: data.postsCount || 0,
    followersCount: data.followersCount || 0,
    followingCount: data.followingCount || 0
  },
  isFollowing: data.isFollowing || false,
  isOwnProfile: data.isOwnProfile || false
})

const userService = {
  // Get user profile by username (OPTIMIZED: single request with all data)
  async getUserProfile(username: string): Promise<UserProfile> {
    const response = await api.get<ApiResponse<UserProfile>>(`/users/profile/${username}`)
    return transformToUserProfile(response.data.data)
  },

  // Get current user profile with stats (OPTIMIZED: single request)
  async getCurrentUserProfile(): Promise<UserProfile> {
    const response = await api.get<ApiResponse<UserProfile>>('/users/me/profile')
    return transformToUserProfile(response.data.data)
  },

  // Get current user (basic info only)
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/users/me')
    return response.data.data
  },

  // Update profile
  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.put<ApiResponse<User>>('/users/me', data)
    return response.data.data
  },

  // Upload profile image
  async uploadProfileImage(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<ApiResponse<{ url: string }>>('/users/me/profile-image', formData, {
      timeout: UPLOAD_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data.data
  },

  // Upload cover image
  async uploadCoverImage(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<ApiResponse<{ url: string }>>('/users/me/cover-image', formData, {
      timeout: UPLOAD_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data.data
  },

  // Get user posts
  async getUserPosts(userId: number, page: number = 0, size: number = 20): Promise<PaginatedResponse<Post>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Post>>>(`/posts/user/${userId}?page=${page}&size=${size}`)
    return response.data.data
  },

  // Follow a user
  async followUser(userId: number): Promise<void> {
    await api.post(`/follows/${userId}`)
  },

  // Unfollow a user
  async unfollowUser(userId: number): Promise<void> {
    await api.delete(`/follows/${userId}`)
  },

  // Check if following
  async isFollowing(userId: number): Promise<boolean> {
    const response = await api.get<ApiResponse<boolean>>(`/follows/status/${userId}`)
    return response.data.data
  },

  // Get followers count
  async getFollowersCount(userId: number): Promise<number> {
    const response = await api.get<ApiResponse<number>>(`/follows/count/followers/${userId}`)
    return response.data.data
  },

  // Get following count
  async getFollowingCount(userId: number): Promise<number> {
    const response = await api.get<ApiResponse<number>>(`/follows/count/following/${userId}`)
    return response.data.data
  },

  // Get followers list
  async getFollowers(userId: number, page: number = 0, size: number = 20): Promise<PaginatedResponse<User>> {
    const response = await api.get<ApiResponse<PaginatedResponse<User>>>(`/follows/followers/${userId}?page=${page}&size=${size}`)
    return response.data.data
  },

  // Get following list
  async getFollowing(userId: number, page: number = 0, size: number = 20): Promise<PaginatedResponse<User>> {
    const response = await api.get<ApiResponse<PaginatedResponse<User>>>(`/follows/following/${userId}?page=${page}&size=${size}`)
    return response.data.data
  },

  // Search users
  async searchUsers(keyword: string, page: number = 0, size: number = 10): Promise<PaginatedResponse<User>> {
    const response = await api.get<ApiResponse<PaginatedResponse<User>>>(`/users/search?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`)
    return response.data.data
  },

  // Update user type (for profile completion)
  async updateUserType(
    userType: 'USER' | 'DOCTOR' | 'HEALTH_WORKER' | 'NGO' | 'NIKSHAY_MITRA'
      | 'SUPPORTER_INDIVIDUAL' | 'SUPPORTER_NGO' | 'SUPPORTER_CORPORATE'
      | 'NUTRITION_SPONSOR' | 'CSR_PARTNER' | 'VOLUNTEER_COMMUNITY',
  ): Promise<User> {
    const response = await api.put<ApiResponse<User>>('/users/me/user-type', { userType })
    return response.data.data
  },

  // Check if profile is completed
  async checkProfileCompleted(): Promise<{ profileCompleted: boolean; hasUserType: boolean }> {
    const response = await api.get<ApiResponse<{ profileCompleted: boolean; hasUserType: boolean }>>('/users/me/profile-completed')
    return response.data.data
  }
}

export default userService